import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { TruncateWithTooltip } from '~/modules/settings/components/TruncateWithTooltip';
import type { Role, UserListItem } from '~settings/api/lib/schemas';
import { GroupInfo } from '~settings/components/GroupInfo';
import { RelativeDate } from '~settings/components/RelativeDate';
import { UserRole } from '~settings/components/UserRole';
import { UserInfo } from '~settings/modules/user/components/UserInfo';

import { ListUserActions } from '../parts/ListUserActions';

interface UseUsersColumns {
  // if not provided, it will show all relevant columns for all user types
  isInternal?: string;
}

export const useUsersColumns = ({ isInternal }: UseUsersColumns = {}) => {
  const { t } = useTranslation('settings');
  const isCurrentUserExternal = useIsExternalUser();
  const [manageExternalUserEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsManageExternalUser,
  ]);

  return useMemo(() => {
    const columnHelper = createColumnHelper<UserListItem>();

    const usersColumns = {
      name: columnHelper.accessor('fullName', {
        id: 'fullName',
        header: () => t('name'),
        cell: (info) => (
          <UserInfo
            title={info.row.original.fullName}
            fullName={info.row.original.fullName}
          />
        ),
        enableSorting: true,
        size: 170,
      }),
      email: columnHelper.accessor('email', {
        id: 'email',
        header: () => t('email'),
        cell: (cell) => (
          <TruncateWithTooltip className="inline-block">
            {cell.getValue()}
          </TruncateWithTooltip>
        ),
        enableSorting: true,
        size: 190,
      }),
      labelGroup: columnHelper.accessor((row) => row.labelGroups, {
        id: 'labelGroups',
        header: () => t('labelGroup.list'),
        cell: (cell) => (
          <GroupInfo groups={(cell.getValue() ?? []).map(({ name }) => name)} />
        ),
        enableSorting: false,
        size: 180,
      }),
      labels: columnHelper.accessor('labels', {
        id: 'labels',
        header: () => t('labels'),
        cell: (cell) => {
          const labels = cell.getValue();

          if (!labels.length) {
            return '-';
          }

          return <GroupInfo groups={labels.map(({ name }) => name)} />;
        },
        enableSorting: false,
        size: 180,
      }),
      role: columnHelper.accessor('labels', {
        id: 'role',
        header: () => t('role'),
        cell: (info) => {
          // get role on shown label 1st. then get other roles in order
          const roles = info.getValue().reduce<Set<Role>>((acc, next) => {
            next.roles.forEach((role) => {
              acc.add(role);
            });

            return acc;
          }, new Set());

          return <UserRole roles={Array.from(roles)} />;
        },
        enableSorting: false,
        minSize: 185,
        size: 185,
      }),
      lastLogin: columnHelper.accessor('lastLogin', {
        id: 'lastLogin',
        header: () => t('lastLogin'),
        cell: (info) => <RelativeDate data={info.getValue()} />,
        enableSorting: true,
        size: 175,
      }),
      controls: columnHelper.display({
        id: 'actions',
        size: 60,
        minSize: 60,
        maxSize: 60,
        cell: ({ row }) => <ListUserActions user={row.original} />,
      }),
    };

    let columnsOrder: (keyof typeof usersColumns)[] = [];

    if (isCurrentUserExternal || isInternal === 'false') {
      columnsOrder = ['name', 'email', 'labels', 'role', 'lastLogin'];
    } else if (isInternal === 'true') {
      columnsOrder = ['name', 'email', 'labelGroup', 'role', 'lastLogin'];
    } else {
      columnsOrder = [
        'name',
        'email',
        'labelGroup',
        'labels',
        'role',
        'lastLogin',
      ];
    }

    if (manageExternalUserEnabled) {
      columnsOrder.push('controls');
    }

    return columnsOrder.map((key) => usersColumns[key]);
  }, [isCurrentUserExternal, isInternal, manageExternalUserEnabled, t]);
};
